import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { money, dateEs, statusLabel } from "@/lib/formatters";
import { DocumentHeader, DocumentFooter } from "@/components/DocumentHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle2, XCircle, MessageCircle, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { DeliveryServicePoliciesContent } from "@/components/DeliveryServicePolicies";

export default function PublicQuote() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/quotes/${token}`);
        setData(r.data);
      } catch (e) {
        setError(apiError(e, "No se pudo cargar la cotización"));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submitAction = async () => {
    if (!confirmAction) return;
    setSubmitting(true);
    try {
      const device = `${navigator.platform || "web"} · ${navigator.userAgent.slice(0, 90)}`;
      const r = await api.post(`/public/quotes/${token}/${confirmAction}`, { device });
      setDone({ action: confirmAction, timestamp: r.data.timestamp });
      const rr = await api.get(`/public/quotes/${token}`);
      setData(rr.data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSubmitting(false);
      setConfirmAction(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--pe-bg)]">
        <Loader2 className="animate-spin text-[color:var(--pe-primary)]" size={32} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--pe-bg)] p-6">
        <div className="pe-card p-8 max-w-md text-center">
          <AlertTriangle className="mx-auto text-red-500 mb-3" size={40} />
          <h2 className="text-2xl font-black text-[color:var(--pe-primary)]">No disponible</h2>
          <p className="text-slate-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const quote = data.quote;
  const company = data.company;
  const isFinal = ["aceptada", "rechazada", "cerrada"].includes(quote.status);
  const waNumber = (company.whatsapp_number || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Hola, tengo dudas sobre la cotización ${quote.folio} de ${company.name}.`);
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  return (
    <div className="min-h-screen bg-[color:var(--pe-bg)]" data-testid="public-quote-page">
      <DocumentHeader company={company} />

      <div className="max-w-4xl mx-auto p-4 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
          <div>
            <div className="pe-label">COTIZACIÓN {quote.type === "producto" ? "DE PRODUCTO" : "DE SERVICIO"}</div>
            <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)] pe-numeric mt-1">{quote.folio}</h1>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-500">Fecha: {dateEs(quote.created_at)}</div>
            <div className="text-sm text-slate-500">Vigencia: {quote.validity_days} días</div>
            <span className={`pe-badge pe-badge-${quote.status} mt-2 inline-flex`}>{statusLabel[quote.status]}</span>
          </div>
        </div>

        <div className="pe-card p-5">
          <div className="pe-label">CLIENTE</div>
          <div className="grid gap-2 sm:grid-cols-2 mt-2 text-sm">
            <div><b>Nombre:</b> {quote.client?.name}</div>
            {quote.client?.rfc && <div><b>RFC:</b> {quote.client.rfc}</div>}
            {quote.client?.contact_name && <div><b>Contacto:</b> {quote.client.contact_name}</div>}
            {quote.client?.phone && <div><b>Teléfono:</b> {quote.client.phone}</div>}
            {quote.client?.address && <div className="sm:col-span-2"><b>Dirección:</b> {quote.client.address}</div>}
          </div>
        </div>

        <div className="mt-4 pe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[color:var(--pe-primary)] text-white text-left">
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wide">#</th>
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wide">Descripción</th>
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wide">Ud.</th>
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wide text-right">Cant.</th>
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wide text-right">P. Unit.</th>
                <th className="px-3 py-2 font-bold text-xs uppercase tracking-wide text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              {quote.items.map((it, i) => {
                const line = (it.quantity || 0) * (it.unit_price || 0) * (1 - (it.discount_pct || 0) / 100);
                return (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2">{it.description}</td>
                    <td className="px-3 py-2">{it.unit}</td>
                    <td className="px-3 py-2 text-right pe-numeric">{it.quantity}</td>
                    <td className="px-3 py-2 text-right pe-numeric">{money(it.unit_price)}</td>
                    <td className="px-3 py-2 text-right pe-numeric font-bold">{money(line)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-end">
          <div className="w-full sm:w-80 pe-card p-4 bg-slate-50">
            <div className="flex justify-between text-sm py-1"><span>Subtotal</span><span className="pe-numeric font-bold">{money(quote.subtotal)}</span></div>
            <div className="flex justify-between text-sm py-1"><span>IVA ({quote.iva_rate}%)</span><span className="pe-numeric font-bold">{money(quote.iva_amount)}</span></div>
            {(quote.isr_rate || 0) !== 0 && (
              <div className="flex justify-between text-sm py-1"><span>ISR ({quote.isr_rate}%)</span><span className="pe-numeric font-bold text-red-600">- {money(Math.abs(quote.isr_amount))}</span></div>
            )}
            <div className="border-t border-[color:var(--pe-border)] mt-1 pt-2 flex justify-between">
              <span className="pe-label">TOTAL</span>
              <span className="pe-numeric font-black text-2xl text-[color:var(--pe-primary)]">{money(quote.total, quote.currency)}</span>
            </div>
          </div>
        </div>

        {quote.notes && (
          <div className="mt-6 pe-card p-5">
            <div className="pe-label mb-2">NOTAS Y CONDICIONES</div>
            <div className="text-sm whitespace-pre-line text-slate-700">{quote.notes}</div>
            {quote.delivery_time && (
              <div className="mt-3 text-sm"><b>Tiempo de entrega/ejecución:</b> {quote.delivery_time}</div>
            )}
          </div>
        )}

        {/* Políticas de entrega y servicio — sección 9.9 del PRD (opcional por cotización, PRD v1.10; off por default) */}
        {quote.include_policies === true && (
          <div className="mt-8" data-testid="public-quote-policies">
            <div className="pe-label mb-4">POLÍTICAS DE ENTREGA Y SERVICIO</div>
            <DeliveryServicePoliciesContent
              warranties={company?.warranties}
              formal={{
                name: company?.name,
                rfc: company?.rfc,
                address: company?.address,
                phone: company?.phone,
                email: company?.email,
              }}
              variant="landing"
            />
          </div>
        )}

        {done && (
          <div className={`mt-8 pe-card p-6 text-center ${done.action === "accept" ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`}>
            {done.action === "accept" ? <CheckCircle2 className="mx-auto text-emerald-600" size={48} /> : <XCircle className="mx-auto text-red-600" size={48} />}
            <div className="mt-3 text-xl font-black text-[color:var(--pe-primary)]">
              {done.action === "accept" ? "¡Cotización aceptada!" : "Cotización rechazada"}
            </div>
            <div className="text-sm text-slate-600 mt-1">Registrado el {dateEs(done.timestamp)}. Gracias.</div>
          </div>
        )}

        {!isFinal && !done && (
          <div className="mt-8">
            <div className="pe-label mb-3">DECISIÓN DEL CLIENTE</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Button className="h-16 text-lg font-black bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setConfirmAction("accept")} data-testid="btn-public-accept">
                <CheckCircle2 size={22} className="mr-2" /> ACEPTAR
              </Button>
              <Button variant="outline"
                      className="h-16 text-lg font-black border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => setConfirmAction("reject")} data-testid="btn-public-reject">
                <XCircle size={22} className="mr-2" /> RECHAZAR
              </Button>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 justify-center">
              <ShieldCheck size={14} /> Su respuesta queda registrada con fecha, IP y dispositivo como evidencia.
            </div>
          </div>
        )}

        {isFinal && !done && (
          <div className={`mt-6 pe-card p-5 ${quote.status === "aceptada" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
            <div className="pe-label">ESTADO REGISTRADO</div>
            <div className="text-sm mt-1">
              Esta cotización fue <b>{statusLabel[quote.status]?.toLowerCase()}</b> el{" "}
              {dateEs(quote.accepted_at || quote.rejected_at)}. Si tiene dudas contacte a la empresa.
            </div>
          </div>
        )}

        {waLink && (
          <div className="mt-6 text-center">
            <a href={waLink} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea856] text-white font-bold py-3 px-6 rounded-md transition-colors"
               data-testid="btn-whatsapp">
              <MessageCircle size={18} /> Contactar por WhatsApp
            </a>
          </div>
        )}
      </div>

      <div className="mt-10">
        <DocumentFooter company={company} />
      </div>

      <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmAction === "accept" ? "Confirmar aceptación" : "Confirmar rechazo"}</DialogTitle>
            <DialogDescription>
              {confirmAction === "accept"
                ? "Está por aceptar formalmente esta cotización. Se registrará con fecha, IP y dispositivo."
                : "Está por rechazar esta cotización. Se registrará con fecha, IP y dispositivo. No podrá revertir esta acción."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={submitting}>Cancelar</Button>
            <Button onClick={submitAction} disabled={submitting}
                    className={confirmAction === "accept"
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-red-600 hover:bg-red-700 text-white"}
                    data-testid="btn-confirm-public-action">
              {submitting ? <Loader2 className="animate-spin" size={18} /> :
                (confirmAction === "accept" ? "Sí, aceptar" : "Sí, rechazar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
